import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'
import { allApplicationsQuery } from '@/api/queries/applications'
import { writeStorage } from '@/lib/storage'

export const APP_SCOPE_STORAGE_KEY = 'appScope'

/**
 * The global application scope from the top bar.
 *
 *   const { appId, application, setAppId } = useAppScope()
 *   // appId is undefined for "All applications"
 *   useQuery(templatesQuery({ app: appId }))
 *
 * The scope lives in the `app` search param on every page (so links are
 * shareable) and is remembered in localStorage for the next visit.
 */
export function useAppScope() {
  const search = useSearch({ strict: false }) as { app?: string }
  const navigate = useNavigate()
  const appId = search.app || undefined
  const apps = useQuery({ ...allApplicationsQuery, enabled: !!appId })
  const application = appId ? apps.data?.find((a) => a.id === appId || a.slug === appId) : undefined

  const setAppId = useCallback(
    (next: string | undefined) => {
      writeStorage(APP_SCOPE_STORAGE_KEY, next ?? null)
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, app: next }),
        replace: true,
      })
    },
    [navigate],
  )

  return { appId, application, setAppId }
}
